import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { ResourceTag } from '../types/resource';

// Conversation-level tags are keyed by chatMessageId (a conversation's anchor message), not by
// resource id — distinct from resource tagging. Mirrors Angular TagService.GetConversationTags /
// TagConversation / UntagResource(withRelated=false, chatMessageId).
const conversationTagsKey = (projectId: string, collectionId: string, chatMessageId: string) =>
  ['conversation-tags', projectId, collectionId, chatMessageId] as const;

export function useConversationTags(projectId: string, collectionId: string, chatMessageId: string) {
  return useQuery({
    queryKey: conversationTagsKey(projectId, collectionId, chatMessageId),
    queryFn: () =>
      api.get<ResourceTag[]>(
        `/project/${projectId}/collection/${collectionId}/chatMessageId/${chatMessageId}/tags`,
      ),
    enabled: Boolean(projectId && collectionId && chatMessageId),
    staleTime: 0, // tags change via apply/remove; always fetch fresh for the active conversation
  });
}

export function useTagConversation(projectId: string, collectionId: string, chatMessageId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post(`/project/${projectId}/collection/${collectionId}/chatMessageId/${chatMessageId}/tag`, {
        name,
        tagRelated: false,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: conversationTagsKey(projectId, collectionId, chatMessageId) }),
  });
}

export function useUntagConversation(
  projectId: string,
  collectionId: string,
  resourceId: string,
  chatMessageId: string,
) {
  const qc = useQueryClient();
  return useMutation({
    // Removal reuses the resource tag-delete endpoint with chatMessageId as a query param
    // (Angular TagService.UntagResource), so the server scopes the removal to the conversation.
    mutationFn: (tagId: string) =>
      api.delete(
        `/project/${projectId}/collection/${collectionId}/resource/${resourceId}/tag/${tagId}` +
          `?withRelated=false&chatMessageId=${chatMessageId}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: conversationTagsKey(projectId, collectionId, chatMessageId) }),
  });
}
